import { useQuery } from "@tanstack/react-query";
import {
  ACTION_TYPES,
  HTTP_AUTH_TYPES,
  HTTP_REQUEST_METHODS,
  HTTP_RESPONSE_TYPES,
  type ActionType,
  type HttpAuthType,
  type HttpRequestMethod,
  type HttpResponseType,
} from "@proclaw/shared";
import { agentSecretsApi } from "@/api/agent-secrets";
import { useTenant } from "@/context/tenant-context";
import { Field, TextInput, Textarea, Select } from "./form-primitives";
import { ExpandableHighlightedTextarea } from "./expandable-highlighted-textarea";

const ACTION_LABELS: Record<ActionType, string> = {
  http_request: "HTTP Request",
  send_email: "Send email",
  wait_for_email_reply: "Wait for email reply",
  post_to_channel: "Post to channel",
  mcp_tool: "Call MCP tool (any tool the agent has access to)",
};

const AUTH_TYPE_LABELS: Record<HttpAuthType, string> = {
  bearer: "Bearer token",
  header: "Custom header",
  query: "Query parameter",
};

const RESPONSE_TYPE_LABELS: Record<HttpResponseType, string> = {
  auto: "Auto — JSON or text",
  json: "JSON",
  text: "Plain text",
  csv: "CSV",
  html: "HTML",
  pdf: "PDF file",
  image: "Image file",
  empty: "Empty — ignore response body",
};

/** Render the mcp_tool args bag for editing — object → pretty JSON, raw
 *  string left as-is (so partially-typed JSON isn't clobbered). */
function argsDisplay(config: Record<string, unknown>): string {
  const args = config.args;
  if (args == null) return "";
  if (typeof args === "string") return args;
  try {
    return JSON.stringify(args, null, 2);
  } catch {
    return "";
  }
}

export function ActionConfig({
  data,
  onChange,
  readOnly,
  variables,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly: boolean;
  variables?: { value: string; label: string; hint?: string }[];
}) {
  const savedActionType = data.actionType as string | undefined;
  const actionType: ActionType =
    savedActionType === "http_get" || savedActionType === "http_post"
      ? "http_request"
      : (savedActionType as ActionType | undefined) ?? "http_request";
  const config = (data.config as Record<string, unknown>) ?? {};
  const textConfig = (key: string) =>
    typeof config[key] === "string" ? (config[key] as string) : "";
  const method: HttpRequestMethod =
    savedActionType === "http_get"
      ? "GET"
      : savedActionType === "http_post"
        ? "POST"
        : HTTP_REQUEST_METHODS.includes(config.method as HttpRequestMethod)
          ? (config.method as HttpRequestMethod)
          : "POST";
  const update = (patch: Record<string, unknown>) =>
    onChange({
      ...data,
      ...(savedActionType === "http_get" || savedActionType === "http_post"
        ? { actionType: "http_request" }
        : {}),
      ...patch,
    });
  const updateConfig = (patch: Record<string, unknown>) =>
    onChange({
      ...data,
      actionType,
      config: {
        ...config,
        ...(savedActionType === "http_get" || savedActionType === "http_post"
          ? { method }
          : {}),
        ...patch,
      },
    });
  const updateWaitConfig = (patch: Record<string, string>) => {
    const { deadlineAt: _legacyDeadline, ...currentConfig } = config;
    update({ config: { ...currentConfig, ...patch } });
  };

  // Credentials for an HTTP action. The node stores only WHICH secret to use;
  // the runner reads the value from Key Vault for one request and keeps it out
  // of step outputs and the run detail page. Listed tenant-wide rather than per
  // agent because resolveHttpActionAuth matches a secretId against any agent in
  // the same company, so a per-agent list would hide valid choices.
  const { tenantId } = useTenant();
  const { data: secrets = [] } = useQuery({
    queryKey: ["company-secrets", tenantId],
    queryFn: () => agentSecretsApi.listForCompany(tenantId),
    enabled: !!tenantId && actionType === "http_request",
  });
  const auth = (config.auth as Record<string, unknown> | undefined) ?? {};
  const authSecretId = typeof auth.secretId === "string" ? auth.secretId : "";
  const authType: HttpAuthType = HTTP_AUTH_TYPES.includes(auth.type as HttpAuthType)
    ? (auth.type as HttpAuthType)
    : "bearer";
  const authName = typeof auth.name === "string" ? auth.name : "";
  const updateAuth = (patch: Record<string, unknown>) => {
    const next: Record<string, unknown> = { ...auth, ...patch };
    // Clearing the secret drops the whole block. Half-filled credentials are a
    // save-time error, and "no credential" is the honest meaning of no secret.
    if (!next.secretId) {
      const { auth: _cleared, ...rest } = config;
      update({ config: rest });
      return;
    }
    // A bearer token carries no header or parameter name. Leaving a stale one
    // behind would be stored and then silently ignored at run time.
    if (next.type === "bearer") delete next.name;
    update({ config: { ...config, auth: next } });
  };

  return (
    <div className="space-y-4">
      <Field label="Action type">
        <Select
          value={actionType}
          onChange={(v) =>
            update({
              actionType: v as ActionType,
              config:
                v === "wait_for_email_reply"
                  ? { waitDuration: "1", waitUnit: "days" }
                  : v === "http_request"
                    ? {
                        method: "POST",
                        ...(textConfig("url") ? { url: textConfig("url") } : {}),
                      }
                  : {},
            })
          }
          disabled={readOnly}
          options={ACTION_TYPES.map((t) => ({ value: t, label: ACTION_LABELS[t] }))}
        />
      </Field>

      {actionType === "wait_for_email_reply" && (
        <div className="grid grid-cols-[1fr_1.25fr] gap-2">
          <Field label="Wait for">
            <TextInput
              value={textConfig("waitDuration")}
              onChange={(value) =>
                updateWaitConfig({
                  waitDuration: value.replace(/\D/g, ""),
                  waitUnit: textConfig("waitUnit") || "days",
                })
              }
              placeholder="1"
              readOnly={readOnly}
            />
          </Field>
          <Field label="Unit">
            <Select
              value={textConfig("waitUnit") || "days"}
              onChange={(value) => updateWaitConfig({ waitUnit: value })}
              disabled={readOnly}
              options={[
                { value: "minutes", label: "Minutes" },
                { value: "hours", label: "Hours" },
                { value: "days", label: "Days" },
              ]}
            />
          </Field>
        </div>
      )}

      {actionType === "http_request" && (
        <>
          <Field label="Method">
            <Select
              value={method}
              onChange={(value) => updateConfig({ method: value })}
              disabled={readOnly}
              options={HTTP_REQUEST_METHODS.map((value) => ({ value, label: value }))}
            />
          </Field>

          <Field label="Credential">
            <Select
              value={authSecretId}
              onChange={(v) => updateAuth({ secretId: v })}
              disabled={readOnly}
              options={[
                { value: "", label: "None, send without signing in" },
                ...secrets.map((s) => ({
                  value: s.id,
                  label: `${s.envName} (${s.agentName})`,
                })),
              ]}
            />
            <p className="mt-1 text-[11px] text-text-muted">
              Only the choice is saved here. The value is read from the vault
              when the step runs and is hidden on the run page.
            </p>
          </Field>

          {authSecretId && (
            <>
              <Field label="Send it as">
                <Select
                  value={authType}
                  onChange={(v) => updateAuth({ type: v })}
                  disabled={readOnly}
                  options={HTTP_AUTH_TYPES.map((value) => ({
                    value,
                    label: AUTH_TYPE_LABELS[value],
                  }))}
                />
              </Field>
              {authType !== "bearer" && (
                <Field
                  label={
                    authType === "query" ? "Query parameter name" : "Header name"
                  }
                >
                  <TextInput
                    value={authName}
                    onChange={(v) => updateAuth({ name: v })}
                    placeholder={authType === "query" ? "api_key" : "X-API-Key"}
                    readOnly={readOnly}
                    mono
                  />
                </Field>
              )}
            </>
          )}

          <Field label="URL">
            <TextInput
              value={textConfig("url")}
              onChange={(v) => updateConfig({ url: v })}
              placeholder="https://example.com/webhook"
              readOnly={readOnly}
              mono
            />
          </Field>
          <Field label="Response type">
            <Select
              value={(textConfig("responseType") as HttpResponseType) || "auto"}
              onChange={(value) => updateConfig({ responseType: value })}
              disabled={readOnly}
              options={HTTP_RESPONSE_TYPES.map((value) => ({
                value,
                label: RESPONSE_TYPE_LABELS[value],
              }))}
            />
            <p className="mt-1 text-[11px] text-text-muted">
              PDF and image responses are saved in the assigned agent&apos;s workspace.
            </p>
          </Field>
          <Field label="Body (JSON)">
            <ExpandableHighlightedTextarea
              value={textConfig("body")}
              onChange={(v) => updateConfig({ body: v })}
              placeholder='{"event": "new_lead"}'
              readOnly={readOnly}
              rows={4}
              variables={variables}
              title="Body (JSON)"
            />
          </Field>
          <p className="text-xs text-text-muted">
            Optional. Leave empty to send no request body. Runs directly from
            the platform without an Agent node.
          </p>
        </>
      )}

      {actionType === "send_email" && (
        <>
          <Field label="To">
            <ExpandableHighlightedTextarea
              value={textConfig("to")}
              onChange={(v) => updateConfig({ to: v.replace(/\r?\n/g, " ") })}
              placeholder="recipient@example.com"
              readOnly={readOnly}
              rows={1}
              variables={variables}
              title="Email recipient"
              singleLine
              className="h-[58px]"
            />
          </Field>
          <Field label="CC (optional)">
            <ExpandableHighlightedTextarea
              value={textConfig("cc")}
              onChange={(v) => updateConfig({ cc: v.replace(/\r?\n/g, " ") })}
              placeholder="reviewer@example.com"
              readOnly={readOnly}
              rows={1}
              variables={variables}
              title="CC recipients"
              singleLine
              className="h-[58px]"
            />
          </Field>
          <Field label="Subject">
            <ExpandableHighlightedTextarea
              value={textConfig("subject")}
              onChange={(v) => updateConfig({ subject: v.replace(/\r?\n/g, " ") })}
              placeholder="Email subject"
              readOnly={readOnly}
              rows={1}
              variables={variables}
              title="Email subject"
              singleLine
              className="h-[58px]"
            />
          </Field>
          <Field label="Body">
            <ExpandableHighlightedTextarea
              value={textConfig("body")}
              onChange={(v) => updateConfig({ body: v })}
              placeholder="Email body"
              readOnly={readOnly}
              rows={4}
              variables={variables}
              title="Email body"
            />
          </Field>
        </>
      )}

      {actionType === "wait_for_email_reply" && (
        <>
          {[
            ["messageId", "Message ID", "{{steps.e03.outputs.message_id}}"],
            ["conversationId", "Conversation ID", "{{steps.e03.outputs.conversation_id}}"],
            ["internetMessageId", "Internet message ID", "{{steps.e03.outputs.internet_message_id}}"],
            ["expectedFrom", "Expected sender", "{{steps.e03.outputs.sent_to}}"],
            ["sentAt", "Sent at", "{{steps.e03.outputs.email_sent_at}}"],
          ].map(([key, label, placeholder]) => (
            <Field key={key} label={label}>
              <ExpandableHighlightedTextarea
                value={textConfig(key)}
                onChange={(value) =>
                  updateConfig({ [key]: value.replace(/\r?\n/g, " ") })
                }
                placeholder={placeholder}
                readOnly={readOnly}
                rows={1}
                variables={variables}
                title={label}
                singleLine
                className="h-[58px]"
              />
            </Field>
          ))}
          {textConfig("deadlineAt") && !textConfig("waitDuration") && (
            <p className="text-[11px] text-amber-500">
              This saved workflow uses the previous exact deadline. Enter a
              duration above to replace it.
            </p>
          )}
          <Field label="Ignored message IDs (JSON)">
            <ExpandableHighlightedTextarea
              value={textConfig("ignoredMessageIds")}
              onChange={(value) => updateConfig({ ignoredMessageIds: value })}
              placeholder='["{{steps.e04.outputs.reply_message_id}}"]'
              readOnly={readOnly}
              rows={3}
              variables={variables}
              title="Ignored message IDs"
            />
          </Field>
          <p className="text-[11px] text-text-muted">
            ProClaw calculates the deadline from the original sent time. The
            platform watches the mailbox and routes timeout as a normal result.
          </p>
        </>
      )}

      {actionType === "post_to_channel" && (
        <>
          <Field label="Channel ID">
            <TextInput
              value={textConfig("channelId")}
              onChange={(v) => updateConfig({ channelId: v })}
              placeholder="uuid of the agent channel"
              readOnly={readOnly}
              mono
            />
          </Field>
          <Field label="Message">
            <ExpandableHighlightedTextarea
              value={textConfig("message")}
              onChange={(v) => updateConfig({ message: v })}
              placeholder="Message to post in the channel"
              readOnly={readOnly}
              rows={3}
              variables={variables}
              title="Channel message"
            />
          </Field>
        </>
      )}

      {actionType === "mcp_tool" && (
        <>
          <Field label="Tool name">
            <TextInput
              value={textConfig("tool")}
              onChange={(v) => updateConfig({ tool: v })}
              placeholder="e.g. proclaw-agent-comms.send_message"
              readOnly={readOnly}
              mono
            />
          </Field>
          <Field label="Arguments (JSON)">
            <ExpandableHighlightedTextarea
              value={argsDisplay(config)}
              onChange={(v) => {
                // Store a parsed object when valid (matches agent-authored
                // workflows); keep the raw text until it parses so the user
                // can type freely.
                let parsed: unknown = v;
                try {
                  parsed = JSON.parse(v);
                } catch {
                  /* keep raw string until valid JSON */
                }
                update({ config: { ...config, args: parsed } });
              }}
              placeholder='{"channel": "ops", "content": "{{steps.summarise.outputs.summary}}"}'
              readOnly={readOnly}
              rows={5}
              variables={variables}
              title="Arguments (JSON)"
            />
          </Field>
          <p className="text-[11px] text-text-muted">
            The executing agent must already have this tool. Args may
            reference prior step outputs via{" "}
            <code>{"{{steps.<id>.outputs.<key>}}"}</code>.
          </p>
        </>
      )}

      <Field label="Description">
        <Textarea
          value={(data.description as string) ?? ""}
          onChange={(v) => update({ description: v })}
          placeholder="Shown on the node body"
          readOnly={readOnly}
          rows={2}
        />
      </Field>
    </div>
  );
}
